import { NavLink } from "react-router";
import {
    Sidebar,
    SidebarContent,
    SidebarGroup,
    SidebarGroupContent,
    SidebarGroupLabel,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarMenuSkeleton,
} from "@/components/ui/sidebar";
import React from "react";
import { useDeviceGroupsOData } from "@/api/custom-hooks/device-group-http-functions";
import { eventEmitter } from "@/utils/eventEmitter";
import { Favourite } from "@/assets";
import { useToggleFavouriteDeviceGroup } from "@/api/device-group-http-functions";
import { handleAndNotifyError } from "@/utils/errorHandling";
import { useSubscribeToEvent } from "@/hooks/use-subscribe-to-event";

export const SitesSidebar = ({
    ref,
    ...props
}: React.ComponentProps<"div"> & {
    side?: "left" | "right";
    variant?: "sidebar" | "floating" | "inset";
    collapsible?: "offcanvas" | "icon" | "none";
}) => {
    const {
        data: sites,
        isLoading,
        isError,
        refetch,
    } = useDeviceGroupsOData({
        queryParams: {
            $orderby: "deviceGroupName asc",
            $select:
                "deviceGroupTag, deviceGroupType, deviceGroupName, isFavourite",
            $filter: "deviceGroupType eq 'Site'",
        },
        query: {
            queryKey: ["sidebar-sites"],
        },
    });

    useSubscribeToEvent("toggleFavouriteDeviceGroup", refetch);

    const { mutateAsync: toggleFavouriteSiteAsync } =
        useToggleFavouriteDeviceGroup();

    const handleUnfavouriteSite = async (deviceGroupTag: string) => {
        try {
            if (deviceGroupTag) {
                await toggleFavouriteSiteAsync({
                    deviceGroupTag: deviceGroupTag,
                    params: {
                        isFavourite: false,
                    },
                });
                eventEmitter.emit("toggleFavouriteDeviceGroup");
                refetch();
            }
        } catch (error) {
            handleAndNotifyError(error);
        }
    };

    return (
        <Sidebar {...props} ref={ref}>
            {props.children}
            <SidebarContent>
                <SidebarGroup>
                    <SidebarGroupLabel>Sites</SidebarGroupLabel>
                    <SidebarGroupContent>
                        <SidebarMenu>
                            <SidebarMenuItem>
                                <SidebarMenuButton asChild>
                                    <NavLink
                                        className={"text-default-color"}
                                        to={`/sites`}
                                        end
                                    >
                                        <span>All sites</span>
                                    </NavLink>
                                </SidebarMenuButton>
                            </SidebarMenuItem>
                        </SidebarMenu>
                    </SidebarGroupContent>
                    <SidebarGroupLabel>Favourites</SidebarGroupLabel>
                    <SidebarGroupContent>
                        <SidebarMenu>
                            {isLoading ? (
                                Array.from({ length: 8 }).map((_, index) => (
                                    <SidebarMenuItem key={index}>
                                        <SidebarMenuSkeleton />
                                    </SidebarMenuItem>
                                ))
                            ) : isError ? (
                                <SidebarMenuItem>
                                    <SidebarMenuButton
                                        className="bg-danger-default text-danger"
                                        onClick={() => refetch()}
                                    >
                                        Failed to load sites, click to try
                                        again.
                                    </SidebarMenuButton>
                                </SidebarMenuItem>
                            ) : !sites?.length ? (
                                <SidebarMenuItem>
                                    <SidebarMenuButton
                                        className="bg-warning-default text-warning"
                                        onClick={() => refetch()}
                                    >
                                        No sites found.
                                    </SidebarMenuButton>
                                </SidebarMenuItem>
                            ) : (
                                sites
                                    .filter((s) => s.isFavourite)
                                    .map(
                                        ({
                                            deviceGroupName,
                                            deviceGroupTag,
                                        }) => (
                                            <SidebarMenuItem
                                                key={deviceGroupTag}
                                            >
                                                <SidebarMenuButton asChild>
                                                    <NavLink
                                                        className={
                                                            "text-default-color"
                                                        }
                                                        to={`/sites/${deviceGroupTag}`}
                                                        end
                                                    >
                                                        <span>
                                                            {deviceGroupName}
                                                        </span>
                                                        <Favourite
                                                            className="ml-auto cursor-pointer"
                                                            onClick={(e) => {
                                                                e.preventDefault(); // Prevent NavLink navigation
                                                                e.stopPropagation(); // Stop event propagation
                                                                handleUnfavouriteSite(
                                                                    deviceGroupTag ??
                                                                        "",
                                                                );
                                                            }}
                                                        />
                                                    </NavLink>
                                                </SidebarMenuButton>
                                            </SidebarMenuItem>
                                        ),
                                    )
                            )}
                        </SidebarMenu>
                    </SidebarGroupContent>
                </SidebarGroup>
            </SidebarContent>
        </Sidebar>
    );
};
